import type { TilePos } from '../../map/MapGen';
import type { ShopItem } from '../run/shop';
import type { EnemyType, EliteAffix } from '../entities/enemyDefs';
import type { ArtifactProc } from '../artifacts/ArtifactDefs';
import type { StatModifier } from '../stats/StatSheet';

/**
 * Seed-placed floor events (design §4.4): opt-in encounters at draw-free
 * candidate tiles, contents pre-rolled at floor start from the `events`
 * stream (kill-order-proof, same philosophy as elite loot pre-rolls).
 * The vault door (§4.2) rides the same placement pipeline.
 */

export type FloorEventType =
  | 'merchant' | 'lootChest' | 'ambush' | 'shrine' | 'vaultDoor'
  | 'boneKey' | 'strongbox' | 'brazierRelay' | 'spring';

/**
 * Brazier relay window (playtest fix 2026-07-26 — a flat 10s was unwinnable
 * on chunk floors): each relay pre-computes `windowTotal` from the ACTUAL
 * BFS walking route between its braziers (draw-free), scaled by SLACK for
 * fighting room and clamped to [MIN, MAX] seconds.
 */
export const BRAZIER_WINDOW_MIN = 15;
export const BRAZIER_WINDOW_MAX = 45;
export const BRAZIER_SLACK = 1.5;

export interface MerchantEvent {
  type: 'merchant';
  pos: TilePos;
  /** Seeded from the events stream; mutates as the player buys. */
  stock: ShopItem[];
}

export interface ChestEvent {
  type: 'lootChest';
  pos: TilePos;
  /** Rolled at floor start: artifact ~60% / gem hoard ~25% / pocket Mend ~15%. */
  content: 'artifact' | 'gems' | 'mend';
  /** Meaningful only when content='artifact'; artifactId null = pool exhausted. */
  drop: { artifactId: string | null; quality: number; proc: ArtifactProc; curseId: string | null };
  opened: boolean;
}

export interface AmbushElite {
  enemyType: EnemyType;
  affix: EliteAffix;
  dropArtifactId: string | null;
  dropQuality: number;
  dropProc: ArtifactProc;
  dropCurseId: string | null;
}

export interface AmbushEvent {
  type: 'ambush';
  pos: TilePos;
  /** Always 3, pre-rolled. */
  elites: AmbushElite[];
  /**
   * Deterministic pattern-stream ordinal base (reserved 800000+ band, per
   * event slot) — trigger order can't perturb another ambush's patterns.
   */
  ordinalBase: number;
  triggered: boolean;
}

export interface ShrineEvent {
  type: 'shrine';
  pos: TilePos;
  /** ONE pre-rolled boon+curse pair — a single opt-in decision. */
  offer: { boonId: string; curseId: string };
  taken: boolean;
}

/** Entrance to the floor's vault side-area ("Reliquary"). */
export interface VaultDoorEvent {
  type: 'vaultDoor';
  pos: TilePos;
}

/**
 * The strongbox's key (ME3): rolled as ONE 'strongbox' slot that consumes
 * TWO candidates — key here, box at the next (farthest-point ordering makes
 * the two-stop route real). Zero draws of its own.
 */
export interface BoneKeyEvent {
  type: 'boneKey';
  pos: TilePos;
  taken: boolean;
}

/** Key-locked box: contents pre-rolled at VAULT-CHEST grade (quality ≥100%). */
export interface StrongboxEvent {
  type: 'strongbox';
  pos: TilePos;
  drop: { artifactId: string | null; quality: number; proc: ArtifactProc; curseId: string | null };
  opened: boolean;
}

/**
 * Movement puzzle (ME3): three braziers on far-apart candidates. Lighting
 * one starts a BRAZIER_WINDOW countdown; all three lit before expiry unseals
 * the reward chest at brazier 0. Expiry resets the braziers — retryable,
 * zero further draws. The reward is a full ChestEvent so World.openChest
 * handles resolution (mend fallback, chestOpened payload) unchanged.
 */
export interface BrazierRelayEvent {
  type: 'brazierRelay';
  /** === braziers[0] (the anchor slot). The reward chest's pos starts here
   *  but MOVES to the last-lit brazier at unseal (World.lightBrazier). */
  pos: TilePos;
  braziers: TilePos[];
  lit: boolean[];
  /** Seconds left to light the rest; 0 = idle (sim-ticked, rng-free). */
  window: number;
  /** Full window granted at first light — BFS-route-derived at roll time. */
  windowTotal: number;
  unsealed: boolean;
  chest: ChestEvent;
}

/** One-use heal through the NORMAL heal path — heal blocks apply. 0 draws. */
export interface SpringEvent {
  type: 'spring';
  pos: TilePos;
  used: boolean;
}

export type FloorEvent =
  | MerchantEvent | ChestEvent | AmbushEvent | ShrineEvent | VaultDoorEvent
  | BoneKeyEvent | StrongboxEvent | BrazierRelayEvent | SpringEvent;

export interface ShrineBoonDef {
  id: string;
  name: string;
  desc: string;
  modifiers: StatModifier[];
}

/**
 * Shrine boons run ~2× the magnitude of the shared curse pool so the trade
 * reads as clearly tempting (curse: −10% dmg vs boon: +25% dmg).
 */
export const SHRINE_BOON_POOL: ShrineBoonDef[] = [
  {
    id: 'honedEdge', name: 'Honed Edge', desc: '+25% damage',
    modifiers: [{ stat: 'damageMult', pct: 0.25 }],
  },
  {
    id: 'ironHeart', name: 'Iron Heart', desc: '+2 max HP',
    modifiers: [{ stat: 'maxHp', flat: 2 }],
  },
  {
    id: 'wideReach', name: 'Wide Reach', desc: '+20% ability area',
    modifiers: [{ stat: 'areaMult', pct: 0.2 }],
  },
  {
    id: 'oiledGears', name: 'Oiled Gears', desc: '−12% cooldowns',
    modifiers: [{ stat: 'cooldownMult', pct: -0.12 }],
  },
];

export const SHRINE_BOONS_BY_ID = new Map(SHRINE_BOON_POOL.map((b) => [b.id, b]));
